import cx from 'classnames';
import IconButton from 'lib/IconButton';
import Button, { Color, Size as SizeButton } from 'lib/Button';
import sidebarSimpleRightIcon from 'assets/icons/sidebar-simple-right.svg';
import mapIcon from 'assets/icons/map.svg';
import overviewIcon from 'assets/icons/columns-plus-right.svg';
import reviewIcon from 'assets/icons/route_frame_lists.svg';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'components/common/types/Store.types';
import { CampaignPlannerMode, CampaignPlannerModeType } from 'store/dealManagement/types';
import { changeFormParams, setDirectSalesAssetList, updatePlannerSettings } from 'store/dealManagement/reducer';
import { FrontEndType } from 'components/common/types/Deal.types';
import useUnsavedChange from 'components/pages/Planner/hooks/useUnsavedChange';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import { useEffect } from 'react';
import { isDirectSalesLinePriced } from 'components/pages/Planner/hooks/utils/getIsDirectSalesLinePriced';
import {
  getEveryLineState,
  getIsAnyLineFetchingAvailability,
  getIsValidDirectSalesAvailabilityRequest,
} from 'store/dealManagement/selectors';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { transformAssetList } from 'components/pages/Planner/hooks/utils/transformAssetList';
import { cancelFunctions } from 'utils/cancelFunctions';
import { DirectSalesStatusOption } from 'components/common/types/DirectSalesCampaign.types';
import { getSolutionSummary } from 'modules/api/directSalesCampaign';
import { useNavigate, useParams } from 'react-router-dom';
import { getAreAllLinesPriced } from 'store/dealManagement/selectors/getAreAllLinesPriced';
import { getDirectSalesStatus } from 'store/dealManagement/selectors/getDirectSalesStatus';
import { getCampaignStatus } from 'store/dealManagement/selectors/getCampaignStatus';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { getMarketId } from 'store/publisher/selectors';
import { getIsLoading } from 'store/dealManagement/selectors/getIsLoading';
import { getCampaignPlannerMode } from 'store/dealManagement/selectors/getCampaignPlannerMode';
import { getIsSummaryPanelOpen } from 'store/dealManagement/selectors/getIsSummaryPanelOpen';
import { getAvailableFrames, getAvailableImpressions } from 'store/dealManagement/selectors/availabilityAssets';
import { FeatureFlags } from 'components/common/types/Features.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';

// eslint-disable-next-line sonarjs/cognitive-complexity
const CampaignActionButtons: React.FC = () => {
  const dispatch = useDispatch();
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);
  const hasProgrammaticOverview = useHasFeatureAccess(FeatureFlags.PLANNER_PROG_OVERVIEW);
  const handleUnsavedChange = useUnsavedChange();

  const marketId = useSelector(getMarketId);
  const isLoading = useSelector(getIsLoading);
  const campaignPlannerMode = useSelector(getCampaignPlannerMode);
  const frontEndType = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.frontEndType);
  const campaignStatus = useSelector(getCampaignStatus);
  const dealId = useSelector(getDealId);
  const isSummaryPanelOpen = useSelector(getIsSummaryPanelOpen);
  const status = useSelector(getDirectSalesStatus);
  const linesStates = useSelector(getEveryLineState);
  const areAllLinesPriced = useSelector(getAreAllLinesPriced);
  const availableFrames = useSelector(getAvailableFrames);
  const availableImpressions = useSelector(getAvailableImpressions);
  const isFetchingAvailability = useSelector(getIsAnyLineFetchingAvailability);

  const isMapMode = campaignPlannerMode === CampaignPlannerMode.MAP;
  const isOverviewMode = campaignPlannerMode === CampaignPlannerMode.OVERVIEW;
  const isReviewMode = campaignPlannerMode === CampaignPlannerMode.REVIEW;
  const showOverviewButton = isDirectSalesCampaignType || hasProgrammaticOverview;

  const isAllocated = isDirectSalesLinePriced(status);
  const isDirectSalesSummaryPanelButtonEnabled = useSelector((state: Store) =>
    isDirectSalesCampaignType ? getIsValidDirectSalesAvailabilityRequest(state) : false,
  );

  const isSummaryPanelButtonEnabled = isDirectSalesCampaignType
    ? isDirectSalesSummaryPanelButtonEnabled
    : Boolean(availableFrames || availableImpressions || isFetchingAvailability);

  useEffect(() => {
    dispatch(updatePlannerSettings({ isSummaryPanelOpen: isSummaryPanelButtonEnabled }));
  }, [isSummaryPanelButtonEnabled]);

  const isActionButtonDisabled = isLoading || isFetchingAvailability;

  const toggleRightSidebar = (): void => {
    dispatch(updatePlannerSettings({ isSummaryPanelOpen: !isSummaryPanelOpen }));
  };

  const handleAssetList = async (): Promise<void> => {
    try {
      dispatch(changeFormParams({ isLoading: true, isEditingDisabled: true }));
      const data = await getSolutionSummary(dealId, marketId, cancelFunctions);

      dispatch(setDirectSalesAssetList(transformAssetList(data)));
      dispatch(
        changeFormParams({
          isLoading: false,
          isEditingDisabled: false,
        }),
      );
    } catch {
      dispatch(
        changeFormParams({
          isLoading: false,
          isEditingDisabled: false,
        }),
      );
    }
  };

  const handleModeChange = async (nextMode: CampaignPlannerModeType): Promise<void> => {
    if (isDirectSalesCampaignType) {
      dispatch(changeFormParams({ isCampaignDrawerOpen: false }));
    }

    if (nextMode === campaignPlannerMode) return;

    handleUnsavedChange(async () => {
      const isCampaignCancelled = linesStates.every((state) => state.status === DirectSalesStatusOption.CANCELLED);
      if (!isCampaignCancelled && isAllocated && dealId && nextMode === CampaignPlannerMode.MAP) {
        await handleAssetList();
      }

      if (isDirectSalesCampaignType) {
        return navigate(`/planner/direct-sales/${campaignId}/${nextMode}`, {
          replace: true,
        });
      }

      if (campaignId) {
        return navigate(`/planner/programmatic/${campaignId}/${nextMode}`, {
          replace: true,
        });
      }

      return navigate(`/planner/programmatic/${nextMode}`, {
        replace: true,
      });
    });
  };

  return (
    <div
      data-test-id="action-buttons"
      className="flex flex-shrink-0 gap-1 px-[8px] justify-end items-center border-l border-neutral-950-opacity-10 w-[var(--right-sidebar-width)]"
    >
      {frontEndType === FrontEndType.PLANNER && (
        <>
          <Button
            color={isMapMode ? Color.TERTIARY : Color.BLACK_AND_WHITE}
            size={SizeButton.SMALL}
            svg={mapIcon}
            label="Map"
            classNames={cx('border leading-tight', { 'border-primary-600': isMapMode })}
            svgClassnames="!w-3 !h-3"
            onClick={() => handleModeChange(CampaignPlannerMode.MAP)}
            isDisabled={(isDirectSalesCampaignType && !dealId) || (!isMapMode && isActionButtonDisabled)}
          />

          {showOverviewButton ? (
            <Button
              color={isOverviewMode ? Color.TERTIARY : Color.BLACK_AND_WHITE}
              size={SizeButton.SMALL}
              svg={overviewIcon}
              label="Overview"
              classNames={cx('border leading-tight', { 'border-primary-600': isOverviewMode })}
              svgClassnames="!w-3 !h-3"
              onClick={() => handleModeChange(CampaignPlannerMode.OVERVIEW)}
              isDisabled={(isDirectSalesCampaignType && !dealId) || (!isOverviewMode && isActionButtonDisabled)}
            />
          ) : null}
        </>
      )}

      {frontEndType === FrontEndType.PLANNER && isDirectSalesCampaignType && (
        <Tooltip
          direction={TooltipDirection.BOTTOM}
          tooltip={!areAllLinesPriced ? 'No priced campaign solution' : undefined}
        >
          <div>
            <Button
              color={isReviewMode ? Color.TERTIARY : Color.BLACK_AND_WHITE}
              size={SizeButton.SMALL}
              svg={reviewIcon}
              label="Review"
              classNames={cx('border leading-tight', { 'border-primary-600': isReviewMode })}
              svgClassnames="!w-3 !h-3"
              onClick={() => handleModeChange(CampaignPlannerMode.REVIEW)}
              isDisabled={
                !dealId ||
                (!isReviewMode && !areAllLinesPriced) ||
                (!isReviewMode && isActionButtonDisabled) ||
                campaignStatus === DirectSalesStatusOption.CANCELLED
              }
            />
          </div>
        </Tooltip>
      )}

      {frontEndType === FrontEndType.PLANNER && (
        <IconButton
          dataTestId="planner-right-sidebar-button"
          icon={sidebarSimpleRightIcon}
          name="Toggle right sidebar"
          handleOnClick={toggleRightSidebar}
          isDisabled={!isSummaryPanelButtonEnabled}
        />
      )}
    </div>
  );
};

export default CampaignActionButtons;
